import { useCopy } from '@/i18n/useCopy'
import { useSessionStore } from '@/store/sessionStore'
import type { SessionState } from '@/voice/types'

/**
 * The session state, in the words a person would use.
 *
 * Shared by the visible status line and the screen-reader status region so the
 * two can never drift apart. When they disagree, the sighted user and the
 * screen-reader user are being told different things about the same moment.
 *
 * Held and muted win over the underlying state, because they are what the
 * person just did, and what they most need confirmed.
 */
export function useStatusLabel(): string {
  const copy = useCopy()
  const state = useSessionStore((store) => store.state)
  const muted = useSessionStore((store) => store.muted)
  const held = useSessionStore((store) => store.held)

  if (held) return copy.ui.status.held
  if (muted && (state === 'listening' || state === 'user_speaking')) return copy.ui.status.muted
  return copy.ui.status[STATUS_KEY[state]]
}

const STATUS_KEY: Record<SessionState, keyof ReturnType<typeof useCopy>['ui']['status']> = {
  idle: 'idle',
  requesting_permission: 'permission',
  connecting: 'connecting',
  listening: 'listening',
  user_speaking: 'userSpeaking',
  processing: 'processing',
  model_speaking: 'modelSpeaking',
  interrupted: 'interrupted',
  reconnecting: 'reconnecting',
  ended: 'ended',
  error: 'error',
}
